import { useMutation, useQuery, useQueryClient, skipToken } from "@tanstack/react-query";
import { Comic, LibraryStatusEntity } from "../../../types";
import { useToast } from "@chakra-ui/react";
import {
  ComicLibraryPayload,
  postAddToLibrary,
  postRemoveFromLibrary,
} from "../../../api/comics";
import { useCallback } from "react";
import { useUserData } from "../../../hooks/useUserData";
import { FUNNEL_TYPES } from "../../funnel/utils/contants";
import { setFunnelModalConfig } from "../../funnel/FunnelModal";
import { FunnelContextArea } from "../../funnel/types";
import { useAccountData } from "../../../hooks/useAccountData";
import { GET_ACCOUNT_ME_QUERY_KEY } from "../../auth/api/authApi";
import {
  trackComicSubscribe,
  trackComicUnsubscribe,
} from "../../../analytics/events/engagement";
import { useCurrentEngagementSource } from "../../../analytics/hooks/useCurrentEngagementSource";
import { NavigationTrigger } from "../../../screens/reader/types";

export function useToggleComicFollow(
  cacheKey: string,
  isHero?: boolean,
  onUpdate?: () => void,
  onRemove?: () => void,
) {
  const { userData } = useUserData();
  const toast = useToast();
  const queryClient = useQueryClient();

  const accountData = useAccountData();
  const { data } = useQuery<Comic>({
    queryKey: [cacheKey],
    queryFn: skipToken,
    networkMode: "offlineFirst",
  });

  const followPayload = {
    comic_user_reading_status_id: 1,
    disable_notifications: false,
    disable_emails: false,
  };

  const eventSource = useCurrentEngagementSource({
    trigger: isHero ? NavigationTrigger.heroCardClicked : undefined,
  });

  const updateComicFavorites = useCallback(
    async (comicFavorites: number[]) => {
      await queryClient.setQueryData([GET_ACCOUNT_ME_QUERY_KEY], () => {
        return {
          data: {
            payload: {
              results: {
                ...accountData,
                comic_favorites: comicFavorites,
              },
            },
          },
        };
      });
    },
    [accountData, queryClient],
  );

  const { mutate: unfollow } = useMutation({
    mutationFn: () => {
      return postRemoveFromLibrary(data?.id ?? 0);
    },
    onMutate: async () => {
      const updatedData = {
        ...data,
        is_favorite: false,
        library_status: null,
      };

      await updateComicFavorites(
        (accountData?.comic_favorites ?? []).filter((id) => id !== data?.id),
      );

      // Update entity cache
      await queryClient.setQueryData([cacheKey], updatedData);

      toast({
        status: "success",
        title: "Removed from Library",
      });
    },
    onError: () => {
      // Revert cache changes on error
      queryClient.invalidateQueries({ queryKey: [cacheKey] });

      toast({
        status: "error",
        title: "Unable to remove from library",
      });
    },
    onSuccess: () => {
      if (data?.id) {
        trackComicUnsubscribe({
          comicId: data.id,
          source: eventSource,
        });
      }
      onRemove?.();
    },
  });

  const { mutate: follow } = useMutation({
    mutationFn: (newStatus?: ComicLibraryPayload) => {
      return postAddToLibrary(data?.id ?? 0, newStatus ?? followPayload);
    },
    onMutate: async (newStatus?: ComicLibraryPayload) => {
      const libraryStatus: LibraryStatusEntity = {
        entity_type: "ComicFavorite",
        comic_id: data?.id ?? 0,
        user_id: userData?.id ?? 0,
        comic_user_reading_status_id:
          newStatus?.comic_user_reading_status_id ?? 1,
        disable_notifications: newStatus?.disable_notifications ? 1 : 0,
        disable_emails: newStatus?.disable_emails ? 1 : 0,
      };

      const updatedData = {
        ...data,
        is_favorite: true,
        library_status: libraryStatus,
      };

      if (data?.id) {
        await updateComicFavorites([
          ...(accountData?.comic_favorites ?? []),
          data.id,
        ]);
      }

      // Update entity cache
      await queryClient.setQueryData([cacheKey], updatedData);

      toast({
        status: "success",
        title: isHero ? "You will be notified!" : "Added to Library",
      });
    },
    onError: () => {
      // Revert cache changes on error
      queryClient.invalidateQueries({ queryKey: [cacheKey] });

      toast({
        status: "error",
        title: "Unable to add to library",
      });
    },
    onSuccess: (_, newStatus) => {
      if (data?.id && !newStatus) {
        const notificationsEnabled = true;

        if (notificationsEnabled) {
          trackComicSubscribe({
            comicId: data.id,
            source: eventSource,
          });
        } else {
          trackComicUnsubscribe({
            comicId: data.id,
            source: eventSource,
          });
        }
      }

      onUpdate?.();
    },
  });

  const toggleFollow = useCallback(
    (newStatus?: ComicLibraryPayload) => {
      if (data?.library_status) {
        unfollow();
      } else {
        follow(newStatus);
      }
    },
    [data?.library_status, follow, unfollow],
  );

  const handleToggleFollow = useCallback(
    (newStatus?: ComicLibraryPayload) => {
      if (!userData) {
        setFunnelModalConfig({
          type: FUNNEL_TYPES.signup,
          context: {
            modalContext: FunnelContextArea.addToLibrary,
          },
          options: {
            onSuccess: () => toggleFollow(newStatus),
          },
        });
      } else {
        toggleFollow(newStatus);
      }
    },
    [toggleFollow, userData],
  );

  return { follow, unfollow, handleToggleFollow };
}
